import React, { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useForm, useFieldArray } from 'react-hook-form';
import { toast } from 'react-toastify';
import { FaEnvelope, FaPhone, FaChild, FaPlus, FaTrash, FaArrowLeft, FaCheckCircle } from 'react-icons/fa';
import api from '../../services/api';
import { burkinaPhoneRule, BURKINA_PHONE_HINT } from '../../utils/phoneUtils';

const ParentAccountRequestPage = () => {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [childChecks, setChildChecks] = useState({});

  const { register, handleSubmit, control, watch, formState: { errors } } = useForm({
    defaultValues: {
      children: [{ matricule: '', date_naissance: '' }],
    },
  });

  const { fields, append, remove } = useFieldArray({ control, name: 'children' });
  const watchedChildren = watch('children');

  const verifyChild = useCallback(async (index, matricule, dateNaissance) => {
    if (!matricule?.trim() || !dateNaissance) {
      setChildChecks((prev) => {
        const next = { ...prev };
        delete next[index];
        return next;
      });
      return null;
    }

    try {
      const res = await api.post('/parent-portal/verify-child', {
        matricule: matricule.trim(),
        date_naissance: dateNaissance,
      });
      setChildChecks((prev) => ({
        ...prev,
        [index]: { ok: true, message: res.data.message },
      }));
      return true;
    } catch (err) {
      const message = err.response?.data?.message || 'Vérification impossible';
      setChildChecks((prev) => ({
        ...prev,
        [index]: { ok: false, message },
      }));
      return false;
    }
  }, []);

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      const results = await Promise.all(
        data.children.map((child, index) =>
          verifyChild(index, child.matricule, child.date_naissance)
        )
      );
      if (results.some((ok) => ok === false)) {
        toast.error('Corrigez les informations des enfants avant de soumettre');
        setLoading(false);
        return;
      }

      const res = await api.post('/parent-portal/account-request', data);
      if (res.data.success) {
        setSubmitted(true);
        toast.success(res.data.message);
      } else {
        toast.error(res.data.message || 'Erreur lors de la demande');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Erreur lors de la demande');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-2xl">✓</span>
          </div>
          <h1 className="text-xl font-bold text-slate-900 mb-2">Demande envoyée</h1>
          <p className="text-slate-600 mb-6">
            Votre demande a été transmise à l'établissement. Vous recevrez un email d'activation si elle est approuvée.
          </p>
          <Link to="/login" className="text-primary-600 hover:underline font-medium">
            Retour à la connexion
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-10 px-4">
      <div className="max-w-lg mx-auto">
        <Link to="/login" className="inline-flex items-center gap-2 text-slate-500 hover:text-slate-700 mb-6 text-sm">
          <FaArrowLeft size={12} /> Retour à la connexion
        </Link>

        <div className="bg-white rounded-2xl shadow-lg p-8">
          <h1 className="text-2xl font-bold text-slate-900 mb-1">Demander un compte parent</h1>
          <p className="text-slate-500 text-sm mb-6">
            Renseignez vos coordonnées (<strong>email et téléphone identiques à ceux enregistrés à l'école</strong>) et les matricules + dates de naissance de <strong>tous</strong> vos enfants inscrits cette année.
          </p>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Nom</label>
                <input {...register('nom', { required: 'Requis' })} className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm" />
                {errors.nom && <p className="text-xs text-red-500 mt-1">{errors.nom.message}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Prénom</label>
                <input {...register('prenom', { required: 'Requis' })} className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm" />
                {errors.prenom && <p className="text-xs text-red-500 mt-1">{errors.prenom.message}</p>}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
              <div className="relative">
                <FaEnvelope className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={14} />
                <input
                  {...register('email', { required: 'Requis', pattern: { value: /^\S+@\S+\.\S+$/, message: 'Email invalide' } })}
                  type="email"
                  className="w-full pl-9 rounded-xl border border-slate-200 px-3 py-2.5 text-sm"
                />
              </div>
              {errors.email && <p className="text-xs text-red-500 mt-1">{errors.email.message}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Téléphone mobile *</label>
              <div className="relative">
                <FaPhone className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={14} />
                <input
                  {...register('telmob', {
                    required: 'Téléphone requis',
                    validate: burkinaPhoneRule.validate,
                  })}
                  className="w-full pl-9 rounded-xl border border-slate-200 px-3 py-2.5 text-sm"
                  placeholder={BURKINA_PHONE_HINT}
                />
              </div>
              {errors.telmob && <p className="text-xs text-red-500 mt-1">{errors.telmob.message}</p>}
              <p className="text-xs text-slate-400 mt-1">Doit correspondre à l'email et au téléphone enregistrés pour le parent.</p>
            </div>

            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="text-sm font-medium text-slate-700 flex items-center gap-2">
                  <FaChild size={14} /> Enfants inscrits
                </label>
                <button
                  type="button"
                  onClick={() => append({ matricule: '', date_naissance: '' })}
                  className="text-xs text-primary-600 hover:text-primary-700 flex items-center gap-1"
                >
                  <FaPlus size={10} /> Ajouter
                </button>
              </div>

              <div className="space-y-3">
                {fields.map((field, index) => (
                  <div key={field.id} className="p-3 bg-slate-50 rounded-xl space-y-2">
                    <div className="flex gap-2 items-start">
                      <div className="flex-1 grid grid-cols-2 gap-2">
                        <div>
                          <input
                            {...register(`children.${index}.matricule`, { required: 'Requis' })}
                            placeholder="Matricule"
                            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                            onBlur={() => verifyChild(
                              index,
                              watchedChildren?.[index]?.matricule,
                              watchedChildren?.[index]?.date_naissance
                            )}
                          />
                        </div>
                        <div>
                          <input
                            {...register(`children.${index}.date_naissance`, { required: 'Requis' })}
                            type="date"
                            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                            onBlur={() => verifyChild(
                              index,
                              watchedChildren?.[index]?.matricule,
                              watchedChildren?.[index]?.date_naissance
                            )}
                          />
                        </div>
                      </div>
                      {fields.length > 1 && (
                        <button type="button" onClick={() => remove(index)} className="text-red-400 hover:text-red-600 p-2">
                          <FaTrash size={12} />
                        </button>
                      )}
                    </div>
                    {childChecks[index] && (
                      <p className={`text-xs flex items-center gap-1 ${childChecks[index].ok ? 'text-green-600' : 'text-red-500'}`}>
                        {childChecks[index].ok && <FaCheckCircle size={11} />}
                        {childChecks[index].message}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-primary-600 text-white rounded-xl font-semibold text-sm hover:bg-primary-700 disabled:opacity-50"
            >
              {loading ? 'Envoi en cours...' : 'Soumettre ma demande'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ParentAccountRequestPage;
